import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LOCAL_STORAGE_KEYS, URL_BASE } from '../../core/constants';
import { HttpClient } from '@angular/common/http';
import { UserProfile } from '../user-profile';
import { HttpUtils } from '../http-utils';
import * as Sentry from '@sentry/browser';
import { map } from 'rxjs/operators';
import { InheritableOnOffFeature } from '../../user-dashboard/shared/on-off-feature.model';
import { RegionFeatureOverrideAbility } from '../../user-dashboard/shared/region-feature-override-ability.model';
import { IdToken } from '@auth0/auth0-angular';
import { UserService } from '../../user-dashboard/shared/user.service';
import { jwtDecode, JwtPayload } from 'jwt-decode';

type Auth0AccessToken = JwtPayload & {
  nickname: string,
  scope: string,
  permissions: string[]
}

@Injectable()
export class AuthenticationService {

  private accessToken: string;
  private idToken: string;
  private permissions: string[] = [];
  private userProfile: BehaviorSubject<UserProfile>;
  private userProfileLoaded: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(private http: HttpClient, private userService: UserService) {
    this.accessToken = this.getAccessToken();
    this.idToken     = this.getIdToken();
    this.initUserProfile();
  }

  public initAuthenticationParams(idToken: IdToken | null, accessToken: string) {
    this.setIdToken(idToken.__raw);
    this.setAccessToken(accessToken);
    return this.userService.getSelfUserProfile(accessToken).subscribe(userProfile =>  {
      const tokenInfo = jwtDecode<Auth0AccessToken>(idToken.__raw)
      userProfile.addTokenInfo(tokenInfo);
      this.setProfile(userProfile);
      this.initUserProfile(userProfile);
      this.userProfileLoaded.next(true);
    });
  }

  public initUserProfile(userProfile?: UserProfile) {
    this.loadUserProfile(userProfile);
    const newUserProfile: UserProfile = this.getUserProfile();
    Sentry.setUser({
      id: newUserProfile.id,
      email: newUserProfile.email,
      carrierId: newUserProfile.carrier ? newUserProfile.carrier.id : null,
      carrierName: newUserProfile.carrier ? newUserProfile.carrier.name : null
    });
  }

  public requestPasswordChange(): Observable<string> {
    const callbackUrl = window.location.origin + '/callback/password-change';
    return this.http.post(URL_BASE + '/api/password-change/request', { callbackUrl }, { headers: HttpUtils.createHeaders() })
      .pipe(
        map((res: any) => res.ticket)
      );
  }

  public isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    const tokenExpired = AuthenticationService.isAccessTokenExpired(accessToken);
    return accessToken && !tokenExpired;
  }

  private static isAccessTokenExpired(accessToken: string|null): boolean {
    if(!accessToken) {
      return true;
    }

    const decodedToken = jwtDecode(accessToken);
    return Math.floor(Date.now() / 1000) >= decodedToken.exp;
  }

  public userProfileBehavior(): BehaviorSubject<UserProfile> {
    return this.userProfile;
  }

  public userProfileLoadedBehavior(): BehaviorSubject<boolean> {
    return this.userProfileLoaded;
  }

  private nextUserProfile(userProfile: UserProfile) {
    if (!this.userProfile) {
      this.userProfile = new BehaviorSubject(userProfile);
    } else if (this.userProfile.value || userProfile) {
      this.userProfile.next(userProfile);
    }
  }

  public getUserProfile(): UserProfile {
    if (!this.userProfile || !this.userProfile.value) {
      this.loadUserProfile();
    }
    return this.userProfile.value || new UserProfile();
  }

  public canShowOverview(): boolean {
    return this.canManageUsers() || this.canManageCarriers();
  }

  public canManageUsers(): boolean {
    return this.hasPermission('manage:users');
  }

  public shouldBeRedirectedToOverview(): boolean {
    return this.hasPermission('redirect:overview');
  }

  public canManageCarriers(): boolean {
    return this.hasPermission('manage:carriers');
  }

  public canManageBlueyonderUserCarrier(): boolean {
    return this.hasPermission('manage:my-carrier-blueyonder-users') && this.userProfile.getValue().carrier?.isUsingBlueyonderTms;
  }

  public canManageBlueyonderUserAllCarriers(): boolean {
    return this.hasPermission('manage:all-carriers-blueyonder-users');
  }

  public canManageBlueyonderUser(): boolean {
   return this.canManageBlueyonderUserCarrier() || this.canManageBlueyonderUserAllCarriers();
  }

  public canManageDrivers(): boolean {
    return this.hasPermission('manage:drivers');
  }

  public canManageCarrierManagers(): boolean {
    return this.hasPermission('manage:carrier-managers');
  }

  public canManageRoles(): boolean {
    return this.hasPermission('manage:roles');
  }

  public canManagePermissions(): boolean {
    return this.hasPermission('manage:permissions');
  }

  public canSearchUsers(): boolean {
    return this.hasPermission('search:user');
  }

  public canManageHotspots(): boolean {
    return this.hasPermission('manage:hotspots');
  }

  public canReadEtaDetails(): boolean {
    return this.hasPermission('read:eta-details');
  }

  public canManageLocalLoads(): boolean {
    return this.canManageSelfLocalLoads() || this.canManageAllLocalLoads();
  }

  public canSeeFindLoadWidget(): boolean {
    return this.canManageLocalLoads() && this.hasPermission('read:find-load-widget');
  }

  public canManageSelfLocalLoads(): boolean {
    const userProfile = this.userProfile.getValue();
    return !!(this.hasPermission('manage:carrier-local-loads') && userProfile.carrier);
  }

  public canManageAllLocalLoads(): boolean {
    return this.hasPermission('manage:all-local-loads');
  }

  public canReadHandovers(): boolean {
    return this.canReadCarrierLoadHandovers() || this.canReadAllLoadHandovers();
  }

  public canAccessPodsValidation(): boolean {
    return this.canUploadPodsValidation() || this.canManagePodsValidation();
  }

  public canUploadPodsValidation(): boolean {
    const carrierFeatures = this.userProfile.getValue()?.carrier?.featureProfile;
    const regionFeatures = this.userProfile.getValue()?.carrier?.region?.features;

    return this.hasPermission('submit:pods-validation') && regionFeatures && carrierFeatures &&
      (
        (// region flag forced to true
          regionFeatures.podValidationMrSyncValue === InheritableOnOffFeature.ON &&
          regionFeatures.podValidationMrSyncOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE
        ) ||
        (// overridable flag activated on carrier level
          carrierFeatures.podValidationMrSyncFeature === InheritableOnOffFeature.ON &&
          regionFeatures.podValidationMrSyncOverrideAbility === RegionFeatureOverrideAbility.OVERRIDABLE
        ) ||
        (// inherited flag activated on region level
          regionFeatures.podValidationMrSyncValue === InheritableOnOffFeature.ON &&
          carrierFeatures.podValidationMrSyncFeature === InheritableOnOffFeature.INHERITED
        )
      ||
        (// region flag forced to true
          regionFeatures.podValidationManufacturerSyncValue === InheritableOnOffFeature.ON &&
          regionFeatures.podValidationManufacturerSyncOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE
        ) ||
        (// overridable flag activated on carrier level
          carrierFeatures.podValidationManufacturerSyncFeature === InheritableOnOffFeature.ON &&
          regionFeatures.podValidationManufacturerSyncOverrideAbility === RegionFeatureOverrideAbility.OVERRIDABLE
        ) ||
        (// inherited flag activated on region level
          regionFeatures.podValidationManufacturerSyncValue === InheritableOnOffFeature.ON &&
          carrierFeatures.podValidationManufacturerSyncFeature === InheritableOnOffFeature.INHERITED
        )
      );
  }

  public canManagePodsValidation(): boolean {
    return this.hasPermission('manage:all-pods-validation');
  }

  public canSeeChepPodValidationWidget(): boolean {
    return this.canManagePodsValidation() && this.hasPermission('read:pod-validation-chep-widget');
  }

  public canSeeCarrierPodValidationWidget(): boolean {
    return this.canUploadPodsValidation() && this.hasPermission('read:pod-validation-carrier-widget');
  }

  public canReadCarrierLoadHandovers(): boolean {
    return this.hasPermission('read:carrier-load-handovers');
  }

  public canReadAllLoadHandovers(): boolean {
    return this.hasPermission('read:load-handovers');
  }

  public canManageLocations(): boolean {
    return this.hasPermission('manage:locations');
  }

  public canReadLocations(): boolean {
    return this.hasPermission('read:locations');
  }

  public canManagePlants(): boolean {
    return this.hasPermission('manage:plants');
  }

  public canManageLocationsOrPlants(): boolean {
    return this.canManagePlants() || this.canManageLocations();
  }

  public canManageCarrierSubcontractors(): boolean {
    return this.hasPermission('manage:carrier-subcontractors');
  }

  public canManageSubcontractors(): boolean {
    return this.hasPermission('manage:subcontractors');
  }

  public canManageRegions(): boolean {
    return this.hasPermission('manage:regions');
  }

  public canReadRegions(): boolean {
    return this.hasPermission('read:regions');
  }

  public canManageReports(): boolean {
    return this.hasPermission('manage:reports');
  }

  public canManageGlobalConfiguration(): boolean {
    return this.hasPermission('manage:global-configurations');
  }

  public canManageConfiguration(): boolean {
    return this.canManageUsers() ||
      this.canManageRoles() ||
      this.canManagePermissions() ||
      this.canManageRegions() ||
      this.canManageLocations() ||
      this.canManageLocationsOrPlants() ||
      this.canManageReports() ||
      this.canManageRegionDecarbonizationTypes()
  }

  public canReadConfiguration(): boolean {
    return this.canReadLocations();
  }

  public canManageLoads(): boolean {
    return this.canViewLoadsManagement() ||
      this.canViewLoadTracking() ||
      this.canUseOverdue() ||
      this.canReadHandovers() ||
      this.canAccessPodsValidation() ||
      this.canManageLocalLoads() ||
      this.canViewTnxDashboard()
  }

  public canMonitorGlobal(): boolean {
    return this.canReadLogEvents() ||
      this.canManageHotspots() ||
      this.canReadForcedCheckin() ||
      this.canReadCarrierForcedCheckin() ||
      this.canManageAssets() ||
      this.canReadReports() ||
      this.canMonitorApi()
  }

  public canReadReports(): boolean {
    return this.hasPermission('read:reports');
  }

  public canManageAssets(): boolean {
    return this.hasPermission('manage:assets');
  }

  public canManageWebhooks(): boolean {
    return this.canManageCarrierWebhooks() || this.canManageAllCarriersWebhooks();
  }

  public canManageAllCarriersWebhooks(): boolean {
    return this.hasPermission('manage:all-carriers-webhooks');
  }

  public canManageCarrierWebhooks(): boolean {
    return this.hasPermission('manage:carrier-webhooks');
  }

  public canManageTruckJourneys() {
    return this.canManageGlobalTruckJourneys() || this.canManageCarrierTruckJourneys();
  }

  public canManageGlobalTruckJourneys() {
    return this.hasPermission('manage:all-truck-journeys');
  }

  public canManageCarrierTruckJourneys() {
    return this.hasPermission('manage:carrier-truck-journeys');
  }

  public canUseOverdue(): boolean {
    return (this.canUseCarrierOverdue() && this.userProfile.getValue()?.carrier?.vendorNumbers !== null &&
        this.userProfile.getValue()?.carrier?.vendorNumbers.length > 0)
      || this.canUseGlobalOverdue();
  }

  public canSeeChepOverdueWidget(): boolean {
    return this.canUseGlobalOverdue() && this.hasPermission('read:overdue-chep-widget');
  }

  public canSeeCarrierOverdueWidget(): boolean {
    return this.canUseCarrierOverdue() && this.userProfile.getValue()?.carrier?.vendorNumbers !== null &&
      this.userProfile.getValue()?.carrier?.vendorNumbers.length > 0 &&
      this.hasPermission('read:overdue-carrier-widget');
  }

  public canUseGlobalOverdue(): boolean {
    return this.hasPermission('use:global-overdue');
  }

  public canUseCarrierOverdue(): boolean {
    return this.hasPermission('use:carrier-overdue');
  }

  public canViewLoadTracking(): boolean {
    return this.canManageCarrierTrips() || this.canManageTrips();
  }

  public canSeeChepLoadTrackingWidget(): boolean {
    return this.canManageTrips() && this.hasPermission('read:load-tracking-chep-widget');
  }

  public canSeeCarrierLoadTrackingWidget(): boolean {
    return this.canManageCarrierTrips() && this.hasPermission('read:load-tracking-carrier-widget');
  }

  public canManageTrips(): boolean {
    return this.hasPermission('manage:trips');
  }

  public canManageCarrierTrips(): boolean {
    return this.hasPermission('manage:carrier-trips') && this.userProfile.getValue()?.featureProfile?.loadTracking;
  }

  public canMonitorApi(): boolean {
    return this.hasPermission('monitor:api');
  }

  public canReadLogEvents(): boolean {
    return this.hasPermission('read:log-events');
  }

  public canReadForcedCheckin(): boolean {
    return this.hasPermission('read:all-plants-force-checkins');
  }

  public canReadCarrierForcedCheckin(): boolean {
    return this.hasPermission('read:carrier-plants-force-checkins');
  }

  public canManageLiteLoginAccesses(): boolean {
    return this.hasPermission('manage:lite-login-accesses');
  }

  public canViewServiceDeskLink(): boolean {
    const userProfile    = this.userProfile.getValue();
    const canAsCarrierManager = this.canViewCarrierLoadsManagement() && userProfile?.carrier?.bluechatEnabled() && userProfile?.hasBluechatEnabled
    const canAsChepManager    = this.canViewAllLoadsManagement() && userProfile?.hasBluechatEnabled;
    return canAsCarrierManager || canAsChepManager;
  }

  public canViewLoadsManagement(): boolean {
    return this.canViewAllLoadsManagement() || this.canViewCarrierLoadsManagement();
  }

  public canSeeChepLoadsManagementWidget(): boolean {
    return this.canViewAllLoadsManagement() && this.hasPermission('read:load-management-chep-widget');
  }

  public canSeeCarrierLoadsManagementWidget(): boolean {
    return this.canViewCarrierLoadsManagement() && this.hasPermission('read:load-management-carrier-widget');
  }

  public canViewAllLoadsManagement(): boolean {
    return this.hasPermission('view:all-loads-management');
  }

  public canViewCarrierLoadsManagement(): boolean {
    return this.hasPermission('view:carrier-loads-management') && this.userProfile.getValue()?.carrier?.isUsingBlueyonderTms;
  }

  public canConfigureBluechat(): boolean {
    return this.hasPermission('configure:bluechat');
  }

  public canSubmitBluechat(): boolean {
    return (this.canSubmitCarrierBluechatNotes() || this.canSubmitAllBluechatNotes()) && this.userProfile.getValue().hasBluechatEnabled;
  }

  public canSubmitAllBluechatNotes(): boolean {
    return this.hasPermission('submit:all-bluechat-notes');
  }

  public canSubmitCarrierBluechatNotes(): boolean {
    return this.hasPermission('submit:carrier-bluechat-notes');
  }

  public canViewTrailerReports(): boolean {
    return this.hasPermission('manage:yard-check-trailers');
  }

  public canViewTnxDashboard(): boolean {
    return this.hasPermission('view:freight-auctions-dashboard');
  }

  public canViewDedicatedFleetManagement(): boolean {
    return this.hasPermission('view:dedicated-fleet-management');
  }

  public canSeeTnxWidget(): boolean {
    return this.hasPermission('read:tnx-widget') && this.canViewTnxDashboard();
  }

  public canManageCarrierImport(): boolean {
    return this.hasPermission('verify:carrier-import');
  }

  public canViewDecarbonizationTypes(): boolean {
    return this.canCreateDecarbonizationTypes() || this.canManageRegionDecarbonizationTypes();
  }

  public canCreateDecarbonizationTypes(): boolean {
    return this.hasPermission('create:decarbo-types');
  }

  public canManageRegionDecarbonizationTypes(): boolean {
    return this.hasPermission('manage:region-decarbo-types');
  }

  public hasPermission(permission: string): boolean {
    if (!this.permissions || this.permissions.length === 0) {
      return false;
    }

    return this.permissions.findIndex(p => p === permission) !== -1;
  }

  public hasAnyPermission(): boolean {
    return !!this.permissions && this.permissions.filter(id => id !== 'email' && id !== 'openid' && id !== 'profile').length > 0;
  }

  public disconnect(): void {
    this.accessToken = null;
    this.idToken     = null;
    this.permissions = [];
    this.nextUserProfile(null);
    for (const key of Object.keys(LOCAL_STORAGE_KEYS)) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS[key]);
    }
    Sentry.setUser({});
  }

  private getAccessToken(): string {
    if (!this.accessToken) {
      this.accessToken = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    }
    return this.accessToken;
  }

  private getIdToken(): string {
    if (!this.idToken) {
      this.idToken = localStorage.getItem(LOCAL_STORAGE_KEYS.ID_TOKEN);
    }
    return this.idToken;
  }

  private setAccessToken(token: string) {
    this.accessToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  private setIdToken(token: string) {
    this.idToken = token;
    localStorage.setItem(LOCAL_STORAGE_KEYS.ID_TOKEN, token);
  }

  private loadUserProfile(userProfile?: UserProfile) {
    if (!this.getIdToken()) {
      this.nextUserProfile(null);
      return;
    }
    const decodedAccessToken = jwtDecode<Auth0AccessToken>(this.getAccessToken())
    const tokenScope = decodedAccessToken.scope || '';
    const tokenPermissions = decodedAccessToken.permissions || [];
    this.permissions = tokenScope
      .split(' ')
      .concat(tokenPermissions)
      .filter((value, index, self) => self.indexOf(value) === index)
      .filter(p => p !== '' && !!p);

    if (!userProfile) {
      const decodedIdToken = jwtDecode(this.getIdToken());
      const newUserProfile = UserProfile.fromDecodedToken(decodedIdToken);
      const localStorageProfile = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.PROFILE)) as UserProfile;
      if (localStorageProfile) {
        newUserProfile.email              = localStorageProfile.email;
        newUserProfile.carrier            = localStorageProfile.carrier;
        newUserProfile.featureProfile     = localStorageProfile.featureProfile;
        newUserProfile.langCode           = localStorageProfile.langCode;
        newUserProfile.hasBluechatEnabled = localStorageProfile.hasBluechatEnabled;
      }
      this.nextUserProfile(newUserProfile);
    } else {
      this.nextUserProfile(userProfile);
    }
  }

  private setProfile(profile: any) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.PROFILE, JSON.stringify(profile));
  }
}
